import { Modal, Box, Typography, IconButton } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import CloseIcon from "@mui/icons-material/Close";
import DOMPurify from "dompurify";

import CustomDropdown from "../../components/custom-dropdown/CustomDropdown";
import BrokenHeartIcon from "../../components/icon-components/BrokenHeartIcon";
import {
  LABEL_SELECT_OPTOUT_REASON,
  PLACEHOLDER_SELECT_OPTOUT_REASON,
} from "../../utility/constants/locale";

import styles from "./OptOutModal.module.scss";
import CustomButton from "../../components/v2/custom-button/CustomButton";

interface OptOutModalProps {
  modalOpen: boolean;
  onCloseModal: () => void;
  handleOptOut: (reasonId: number) => void;
  optOutReasons: { label: string; value: number }[];
  message: string;
}

const OptOutModal: React.FC<OptOutModalProps> = ({
  modalOpen,
  onCloseModal,
  handleOptOut,
  optOutReasons,
  message,
}) => {
  const { t } = useTranslation();
  const [selectedReason, setSelectedReason] = useState<number>(0);

  useEffect(() => {
    if (!modalOpen) {
      setSelectedReason(0);
    }
  }, [modalOpen]);

  const handleDropdownChange = (value: string | number) => {
    setSelectedReason(typeof value === "number" ? value : parseInt(value, 10));
  };

  const handleConfirmOptOut = () => {
    if (selectedReason !== 0) {
      handleOptOut(selectedReason);
    }
  };

  const sanitizedContent = DOMPurify.sanitize(message);

  return (
    <Modal open={modalOpen} onClose={onCloseModal}>
      <Box className={styles.modalContainer}>
        <Box className={styles.header}>
          <Box className={styles.titleWrapper}>
            <BrokenHeartIcon height="46px" width="41px" />
            <Typography variant="h4" className={styles.title}>
              {t("optOutModal.title")}
            </Typography>
          </Box>
          <IconButton
            onClick={onCloseModal}
            sx={{ position: "absolute" }}
            className={styles.closeButton}
          >
            <CloseIcon fontSize="large" />
          </IconButton>
        </Box>

        <Box className={styles.body}>
          <Box
            sx={{ mb: 2 }}
            dangerouslySetInnerHTML={{ __html: sanitizedContent }}
            className={styles.modalText}
          />

          <Box className={styles.dropdownWrapper}>
            <CustomDropdown
              placeholder={t("select", PLACEHOLDER_SELECT_OPTOUT_REASON)}
              inputFormTitle={t("reasonForOptOut", LABEL_SELECT_OPTOUT_REASON)}
              value={selectedReason}
              changeTrigger={handleDropdownChange}
              options={optOutReasons}
              isRequired
            />
          </Box>

          <Box className={styles.buttonGroup}>
            <CustomButton
              variant="outlined"
              text={t("cancel")}
              onClick={onCloseModal}
              sx={{ minWidth: "120px", borderRadius: "24px" }}
            />
            <CustomButton
              variant="contained"
              text={t("optOutModal.confirm")}
              onClick={handleConfirmOptOut}
              disabled={selectedReason === 0}
              sx={{ minWidth: "120px", borderRadius: "24px" }}
            />
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default OptOutModal;
